// Dates and times for the booking pages, always in the LEAD's zone. The server
// sends instants; every label a lead reads is made here from their zone, so
// changing the zone relabels everything without a second source of truth.

export function browserZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function allZones() {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [
      "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York", "America/Toronto",
      "America/Sao_Paulo", "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin",
      "Europe/Amsterdam", "Europe/Madrid", "Europe/Rome", "Europe/Zurich", "Europe/Stockholm",
      "Asia/Jerusalem", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
    ];
  }
}

function offsetName(zone, at = new Date()) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part ? part.value : "";
  } catch {
    return "";
  }
}

/** "London (GMT+1)" */
export function zoneLabel(zone) {
  const city = String(zone).split("/").pop().replace(/_/g, " ");
  const offset = offsetName(zone);
  return offset ? `${city} (${offset})` : city;
}

/** "(GMT+1)" alone, for the booked header's "London time (GMT+1)". */
export function zoneCity(zone) {
  return String(zone).split("/").pop().replace(/_/g, " ");
}
export { offsetName };

// Twelve- or twenty-four-hour, by the lead's own locale.
const TWELVE_HOUR = (() => {
  try {
    const cycle = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions().hourCycle;
    return cycle === "h12" || cycle === "h11";
  } catch {
    return false;
  }
})();

/** "8:00", or "8:00 am" where the lead's locale reads twelve-hour. */
export function clock(iso, zone) {
  const date = new Date(iso);
  if (TWELVE_HOUR) {
    return new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit", hour12: true })
      .format(date)
      .replace(" AM", " am")
      .replace(" PM", " pm");
  }
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(date);
  const hour = Number(parts.find((p) => p.type === "hour").value);
  const minute = parts.find((p) => p.type === "minute").value;
  return `${hour}:${minute}`;
}

/** "Tue, Oct 6" */
export function longDay(iso, zone) {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "short", month: "short", day: "numeric" }).format(
    new Date(iso)
  );
}

/** The lead's date of an instant, as YYYY-MM-DD — the key slots are grouped by. */
export function dayKey(iso, zone) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: zone, dateStyle: "short" }).format(new Date(iso));
}

/** { weekday: "TUE", day: "6", month: "OCT" } for a YYYY-MM-DD, read at noon UTC so it never slips. */
export function dayParts(key) {
  const date = new Date(`${key}T12:00:00Z`);
  const fmt = (opts) => new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...opts }).format(date).toUpperCase();
  return { weekday: fmt({ weekday: "short" }), day: fmt({ day: "numeric" }), month: fmt({ month: "short" }) };
}

/** "Tue, Oct 6 · 8:00–8:30", or "· 10:00–10:30 am" with one meridiem when both share it. */
export function whenLabel(start, end, zone) {
  let from = clock(start, zone);
  const to = clock(end, zone);
  const meridiem = / (am|pm)$/;
  const a = from.match(meridiem);
  const b = to.match(meridiem);
  if (a && b && a[1] === b[1]) from = from.replace(meridiem, "");
  return `${longDay(start, zone)} · ${from}–${to}`;
}

/** Slots grouped by the lead's own date, in order. */
export function groupByDay(slots, zone) {
  const days = new Map();
  for (const slot of slots || []) {
    const key = dayKey(slot.start, zone);
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(slot);
  }
  return days;
}

export const pad2 = (n) => String(n).padStart(2, "0");
